/**
 * Extract the last ```sql ... ``` block from markdown (the final generated/validated SQL).
 * Used to store in nlp_messages.generated_sql for assistant messages.
 * @param {string} markdown
 * @returns {string|null} SQL string or null if none found
 */
export const extractLastSqlFromMarkdown = (markdown) => {
  if (!markdown || typeof markdown !== "string") return null;
  const sqlCodeBlockRegex = /```sql\n([\s\S]*?)\n```/g;
  const matches = [...markdown.matchAll(sqlCodeBlockRegex)];
  if (matches.length === 0) return null;
  const last = matches[matches.length - 1][1];
  return last ? last.trim() : null;
};

/**
 * Parse SQL execution results from markdown text
 * Extracts: SQL query, success status, row count, execution time, columns, and data rows
 */
export const parseSqlExecutionFromMarkdown = (markdown) => {
  if (!markdown || typeof markdown !== "string") {
    return null;
  }

  const result = {
    sql: "",
    // originalQuery is optional; when present (or inferred) it represents the full,
    // unmodified SQL, while sql may be a displayed/limited variant.
    originalQuery: "",
    success: false,
    row_count: 0,
    execution_time: 0,
    columns: [],
    data: [],
    error: "",
  };

  // Extract SQL query from code blocks
  const sqlCodeBlockRegex = /```sql\n([\s\S]*?)\n```/g;
  const sqlMatches = [...markdown.matchAll(sqlCodeBlockRegex)];
  if (sqlMatches.length > 0) {
    // Get the last SQL code block (most recent execution)
    result.sql = sqlMatches[sqlMatches.length - 1][1].trim();
  }

  // Check for success message
  const successMatch = markdown.match(/✅\s*\*\*Query executed successfully!\*\*\s*Returned\s+(\d+)\s+row/i);
  if (successMatch) {
    result.success = true;
    result.row_count = parseInt(successMatch[1], 10) || 0;
  } else {
    // Check for error message
    const errorMatch = markdown.match(/❌\s*\*\*Query failed:\*\*\s*(.+?)(?:\n|$)/i);
    if (errorMatch) {
      result.success = false;
      result.error = errorMatch[1].trim();
      return result;
    }
    // If no success or error found, return null (not a SQL execution result)
    return null;
  }

  // Extract execution time
  const timeMatch = markdown.match(/⏱️\s*\*\*Execution time:\*\*\s*([\d.]+)s/i);
  if (timeMatch) {
    result.execution_time = parseFloat(timeMatch[1]) || 0;
  }

  // Extract columns from the "Columns:" line
  const columnsMatch = markdown.match(/📊\s*\*\*Columns:\*\*\s*(.+?)(?:\n|$)/i);
  if (columnsMatch) {
    result.columns = columnsMatch[1]
      .split(",")
      .map((col) => col.trim())
      .filter((col) => col.length > 0);
  }

  // Parse markdown table
  // Match "## Data Preview" followed by table, stopping at note like "*Showing first 20 of X rows*" or end
  const tableMatch = markdown.match(/##\s*Data Preview\s*\n\n([\s\S]*?)(?:\n\*Showing|$)/);
  if (tableMatch) {
    const tableText = tableMatch[1];
    const lines = tableText.split("\n").filter((line) => {
      const trimmed = line.trim();
      // Filter out empty lines and separator lines (lines with only dashes and pipes)
      return trimmed.length > 0 && !trimmed.match(/^\|\s*[-:]+\s*\|/);
    });

    if (lines.length >= 1) {
      // First line is header
      const headerLine = lines[0];
      const headerCells = parseTableRow(headerLine);
      
      // If we didn't get columns from the "Columns:" line, use table header
      if (result.columns.length === 0 && headerCells.length > 0) {
        result.columns = headerCells;
      }

      // Remaining lines are data rows (skip separator if present)
      const dataRows = [];
      let startIdx = 1;
      // Check if second line is a separator (all dashes)
      if (lines.length > 1 && lines[1].match(/^\|\s*[-:]+\s*\|/)) {
        startIdx = 2;
      }
      
      for (let i = startIdx; i < lines.length; i++) {
        const rowCells = parseTableRow(lines[i]);
        if (rowCells.length > 0) {
          // Convert to object format
          const rowObj = {};
          result.columns.forEach((col, idx) => {
            rowObj[col] = rowCells[idx] || "";
          });
          dataRows.push(rowObj);
        }
      }
      result.data = dataRows;
    }
  }

  return result;
};

/**
 * Parse a markdown table row into an array of cell values
 */
const parseTableRow = (row) => {
  if (!row || !row.startsWith("|") || !row.endsWith("|")) {
    return [];
  }

  // Remove leading and trailing pipes, then split by pipe
  const cells = row
    .slice(1, -1)
    .split("|")
    .map((cell) => {
      // Trim and unescape pipe characters
      return cell.trim().replace(/\\\|/g, "|");
    })
    .filter((cell) => cell.length > 0 || cell === ""); // Keep empty cells

  return cells;
};

